<template>
  <div>
    <FilterBrokerPreRegister
      v-show="activeList"
      :initialFilters="filters"
      @search="handleSearch"
      @exportFile="exportFile"
    />

    <vs-card>
      <BrokerPreRegisterDataList
        v-if="activeList"
        :key="pagination.page"
        :itens="items"
        :pagination.sync="pagination"
        :totalCount="totalCount"
        @edit="showRegisterDetails"
        @logs="openLogs"
        @manualApprove="manualApprove"
      />

      <BrokerPreRegisterEdit
        v-if="activeEdit"
        :data="registerDetails"
        @search="getItems"
      />

      <BrokerPreRegisterLogs
        :logs="logs"
        :active="activeLogs"
        @close="closeLogs"
      />
    </vs-card>
  </div>
</template>

<script>
import brokersPreRegister from '../../../../services/brokers-pre-register';
import functions from '../../../../utils/functions';
import notify from '../../../../utils/notify';
import FilterBrokerPreRegister from '../../../filtros/FilterBrokerPreRegister.vue';
import BrokerPreRegisterDataList from './components/BrokerPreRegisterDataList.vue';
import BrokerPreRegisterEdit from './components/BrokerPreRegisterEdit.vue';
import BrokerPreRegisterLogs from './components/BrokerPreRegisterLogs.vue';
import { BROKER_PRE_REGISTER_STATUS } from './constants/broker.pre.register';

export default {
  name: 'BrokerPreRegister',
  components: {
    BrokerPreRegisterDataList,
    BrokerPreRegisterEdit,
    BrokerPreRegisterLogs,
    FilterBrokerPreRegister,
  },
  data() {
    return {
      onScreenComponent: 'BrokerPreRegisterDataList',
      registerDetails: null,
      items: [],
      activeLogs: false,
      logs: [],
      isUpdatingUrl: false,
      debounceTimer: null,
      isFirstLoad: true,
      filters: {
        statuses: [
          BROKER_PRE_REGISTER_STATUS.PENDING,
          BROKER_PRE_REGISTER_STATUS.PENDING_DOCUMENTATION,
          BROKER_PRE_REGISTER_STATUS.MANUAL_REGISTRATION,
          BROKER_PRE_REGISTER_STATUS.EXTERNAL_REGISTRATION,
        ],
      },
      pagination: { itemsPerPage: 50, page: 1 },
      totalCount: 0,
    };
  },
  watch: {
    'pagination.page'(newVal, oldVal) {
      if (this.isUpdatingUrl) return;

      this.updateUrlWithFilters();
      this.getItems();
    },
    'pagination.itemsPerPage'(newVal, oldVal) {
      if (this.isUpdatingUrl) return;

      this.pagination.page = 1;
      this.updateUrlWithFilters();
      this.getItems();
    },
    items: {
      handler() {
        if (!this.registerDetails || !this.items) return;
        var found = this.items.find(function (item) {
          return (
            item.id ===
            (this && this.registerDetails ? this.registerDetails.id : null)
          );
        }, this);
        this.registerDetails = found;
      },
      deep: true,
    },
  },
  computed: {
    activeList() {
      return this.onScreenComponent === 'BrokerPreRegisterDataList';
    },
    activeEdit() {
      return this.onScreenComponent === 'BrokerPreRegisterEdit';
    },
    filtersWithPagination() {
      return Object.assign({}, this.filters, {
        pagination: Object.assign({}, this.pagination),
      });
    },
  },
  mounted() {
    this.loadFiltersFromUrl();

    this.$nextTick(() => {
      this.updateUrlWithFilters();
      this.getItems();
      this.isFirstLoad = false;
    });
  },
  methods: {
    loadFiltersFromUrl() {
      const query = this.$route.query;
      if (!query || Object.keys(query).length === 0) return;

      this.isUpdatingUrl = true;

      const newFilters = {
        statuses: [],
      };

      if (query.broker) newFilters.broker = query.broker;
      if (query.cnpj) newFilters.cnpj = query.cnpj;
      if (query.consultancy) newFilters.consultancy = query.consultancy;

      if (query.statuses) {
        try {
          newFilters.statuses = Array.isArray(query.statuses)
            ? [...query.statuses]
            : [query.statuses];
        } catch (e) {
          newFilters.statuses = this.filters.statuses;
        }
      }

      if (query.portals) {
        try {
          newFilters.portals = Array.isArray(query.portals)
            ? query.portals
            : [query.portals];
        } catch (e) {
          newFilters.portals = [];
        }
      }

      if (
        query.dateType ||
        query.initialDate ||
        query.endDate ||
        query.startDate
      ) {
        const parseLocalDate = (dateStr) => {
          if (!dateStr) return null;
          const [year, month, day] = dateStr.split('-');
          return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        };

        newFilters.dateRange = {
          dateType: query.dateType || null,
          initialDate: parseLocalDate(query.initialDate || query.startDate),
          endDate: parseLocalDate(query.endDate),
        };
      }

      if (query.page) {
        const newPage = parseInt(query.page) || 1;
        this.$set(this.pagination, 'page', newPage);
      }

      if (query.itemsPerPage) {
        const newItemsPerPage = parseInt(query.itemsPerPage) || 50;
        this.$set(this.pagination, 'itemsPerPage', newItemsPerPage);
      }

      this.filters = Object.assign({}, this.filters, newFilters);

      this.$nextTick(() => {
        this.isUpdatingUrl = false;
      });
    },
    updateUrlWithFilters() {
      const query = {};

      if (this.filters.broker) query.broker = this.filters.broker;
      if (this.filters.cnpj) query.cnpj = this.filters.cnpj;
      if (this.filters.consultancy)
        query.consultancy = this.filters.consultancy;

      if (this.filters.statuses && this.filters.statuses.length > 0) {
        query.statuses = this.filters.statuses;
      }

      if (this.filters.portals && this.filters.portals.length > 0) {
        query.portals = this.filters.portals;
      }

      if (this.filters.dateRange) {
        if (this.filters.dateRange.dateType) {
          query.dateType = this.filters.dateRange.dateType;
        }
        if (this.filters.dateRange.initialDate) {
          const date =
            this.filters.dateRange.initialDate instanceof Date
              ? this.filters.dateRange.initialDate
              : new Date(this.filters.dateRange.initialDate);
          query.initialDate = date.toISOString().split('T')[0];
        }
        if (this.filters.dateRange.endDate) {
          const date =
            this.filters.dateRange.endDate instanceof Date
              ? this.filters.dateRange.endDate
              : new Date(this.filters.dateRange.endDate);
          query.endDate = date.toISOString().split('T')[0];
        }
      }

      query.page = this.pagination.page;
      query.itemsPerPage = this.pagination.itemsPerPage;

      this.$router.replace({ query }).catch(() => {});
    },
    handleSearch(payload) {
      this.filters = { ...payload };

      if (payload.statuses) {
        this.filters.statuses = [...payload.statuses];
      }

      this.pagination.page = 1;

      this.updateUrlWithFilters();
      this.getItems();
    },
    async exportFile() {
      this.$vs.loading();
      try {
        const response =
          await brokersPreRegister.getBrokersPreRegisterExportByFilters(
            this.filtersWithPagination,
          );
        const blob = new Blob([response], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = 'pre-cadastro-corretoras.csv';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        notify.notifyFail(this.$vs, 'Erro ao fazer download do arquivo.');
      } finally {
        this.$vs.loading.close();
      }
    },
    async getItems() {
      try {
        this.$vs.loading();

        const data = await brokersPreRegister.getBrokersPreRegister(
          this.filtersWithPagination,
        );

        this.totalCount = data.totalCount;
        this.items = data.items;
      } catch (error) {
        notify.notifyFail(this.$vs, error.message);
      } finally {
        this.$vs.loading.close();
      }
    },
    showRegisterDetails(data) {
      this.onScreenComponent = 'BrokerPreRegisterEdit';
      this.registerDetails = data;
    },
    openLogs(id) {
      const register = this.items.find(function (item) {
        return item.id === id;
      });
      this.logs = functions.organizarEmDias(
        register.logs
          .map(function (log) {
            var copy = Object.assign({}, log);
            copy.createdAt = functions.formatDateAndHour(
              log.createdAt,
            );
            return copy;
          })
          .reverse(),
      );
      this.activeLogs = true;
    },
    closeLogs() {
      this.activeLogs = false;
    },
    async manualApprove(id) {
      try {
        this.$vs.loading();
        await brokersPreRegister.manualApprove(id);
        await this.getItems();
        notify.notifySuccess(this.$vs, 'Aprovado manualmente');
      } catch (error) {
        notify.notifyFail(this.$vs, 'Erro ao aprovar manualmente');
      } finally {
        this.$vs.loading.close();
      }
    },
  },
};
</script>
